type AudioContextConstructor = new (
	contextOptions?: AudioContextOptions,
) => AudioContext;

declare global {
	interface Window {
		webkitAudioContext?: AudioContextConstructor;
	}
}

const keySounds = ["keydown.mp3", "keyup.mp3"] as const;
const effectSounds = ["boom.mp3", "wham.mp3", "mow.mp3"] as const;
type EffectSound = (typeof effectSounds)[number];

export type KeySound = (typeof keySounds)[number];

export class HomepageAudio {
	private audioContext?: AudioContext;

	private get context(): AudioContext {
		return (this.audioContext ??= this.createContext());
	}

	private readonly AudioContextClass: AudioContextConstructor;

	private readonly keyAudioBuffers = new Map<KeySound, AudioBuffer>();

	private readonly keyAudioLoads = new Map<KeySound, Promise<void>>();

	private readonly activeKeySounds: AudioBufferSourceNode[] = [];

	private readonly fallbackKeySounds: HTMLAudioElement[] = [];

	private readonly maxActiveKeySounds = 6;

	private readonly effectAudio = new Map<EffectSound, HTMLAudioElement>();

	constructor(private readonly keyAudioUrls: Record<KeySound, string>) {
		// SAFETY: This optional browser API is missing from our DOM types and checked below.
		const audioSession = (
			navigator as Navigator & { audioSession?: { type: string } }
		).audioSession;
		if (audioSession) {
			// Keep incidental sounds out of iOS's Now Playing controls.
			audioSession.type = "ambient";
		}
		this.AudioContextClass = window.AudioContext ?? window.webkitAudioContext;
	}

	preloadKeys(): void {
		for (const sound of keySounds) {
			void this.preloadKeySound(sound);
		}
	}

	private createContext(): AudioContext {
		return new this.AudioContextClass({ latencyHint: "interactive" });
	}

	private decodeAudio(data: ArrayBuffer): Promise<AudioBuffer> {
		return new Promise<AudioBuffer>((resolve, reject) => {
			const decoded = this.context.decodeAudioData(data, resolve, reject);
			decoded?.then(resolve, reject);
		});
	}

	private preloadKeySound(sound: KeySound): Promise<void> {
		let load = this.keyAudioLoads.get(sound);
		if (!load) {
			load = fetch(this.keyAudioUrls[sound])
				.then((response) => {
					if (!response.ok) {
						throw new Error(`Failed to load ${sound}: ${response.status}`);
					}
					return response.arrayBuffer();
				})
				.then((data) => this.decodeAudio(data))
				.then((buffer) => {
					this.keyAudioBuffers.set(sound, buffer);
				})
				.catch((error) => console.warn(error));
			this.keyAudioLoads.set(sound, load);
		}
		return load;
	}

	private stopKeySound(source: AudioBufferSourceNode): void {
		const index = this.activeKeySounds.indexOf(source);
		if (index !== -1) {
			this.activeKeySounds.splice(index, 1);
		}
		// Replacing this single handler prevents a second cleanup after stop().
		// oxlint-disable-next-line unicorn/prefer-add-event-listener
		source.onended = null;
		try {
			source.stop();
		} catch {
			// It may already have ended between events.
		}
		source.disconnect();
	}

	private startKeySound(sound: KeySound): boolean {
		const buffer = this.keyAudioBuffers.get(sound);
		if (!buffer) {
			return false;
		}

		while (this.activeKeySounds.length >= this.maxActiveKeySounds) {
			const oldest = this.activeKeySounds[0];
			if (oldest) {
				this.stopKeySound(oldest);
			}
		}

		const source = this.context.createBufferSource();
		source.buffer = buffer;
		source.connect(this.context.destination);
		// oxlint-disable-next-line unicorn/prefer-add-event-listener -- Cleared by stopKeySound.
		source.onended = () => {
			const index = this.activeKeySounds.indexOf(source);
			if (index !== -1) {
				this.activeKeySounds.splice(index, 1);
			}
			source.disconnect();
		};
		this.activeKeySounds.push(source);
		source.start();
		return true;
	}

	wakeKeyAudio(): void {
		this.preloadKeys();
		if (this.context.state === "interrupted") {
			for (const source of [...this.activeKeySounds]) {
				this.stopKeySound(source);
			}
			this.audioContext = this.createContext();
		}

		if (this.context.state !== "running") {
			this.context.resume().catch((error) => console.warn(error));
		}
	}

	playKeySound(sound: KeySound): boolean {
		if (this.context.state !== "running") {
			this.wakeKeyAudio();
			return false;
		}
		return this.startKeySound(sound);
	}

	private stopFallbackKeySound(element: HTMLAudioElement): void {
		const index = this.fallbackKeySounds.indexOf(element);
		if (index !== -1) {
			this.fallbackKeySounds.splice(index, 1);
		}
		element.pause();
		element.removeAttribute("src");
	}

	playFallbackKeySound(sound: KeySound): void {
		while (this.fallbackKeySounds.length >= this.maxActiveKeySounds) {
			const oldest = this.fallbackKeySounds[0];
			if (oldest) {
				this.stopFallbackKeySound(oldest);
			}
		}

		const element = new Audio(this.keyAudioUrls[sound]);
		element.preload = "auto";
		element.addEventListener(
			"ended",
			() => this.stopFallbackKeySound(element),
			{ once: true },
		);
		this.fallbackKeySounds.push(element);
		element.play().catch((error) => {
			this.stopFallbackKeySound(element);
			if (
				!(error instanceof DOMException && error.name === "NotAllowedError")
			) {
				console.warn(error);
			}
		});
	}

	preloadEffects(): void {
		for (const filename of effectSounds) {
			if (this.effectAudio.has(filename)) {
				continue;
			}
			const element = new Audio(`/audio/${filename}`);
			element.preload = "auto";
			this.effectAudio.set(filename, element);
			element.load();
		}
	}

	playEffect(filename: EffectSound): void {
		const preloaded = this.effectAudio.get(filename);
		// Separate players allow overlapping effects while reusing the cached resource.
		const element = new Audio(preloaded?.src ?? `/audio/${filename}`);
		element.play().catch((error) => console.warn(error));
	}
}
